import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, TrendingUp, GraduationCap, HelpCircle, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface QuizQuestion {
  question: string;
  options: { label: string; points: number }[];
}

const FOOTBALL_QUIZ: QuizQuestion[] = [
  {
    question: 'A defense is in "Cover 3." What does that tell you about the deep part of the field?',
    options: [
      { label: 'Three defenders each cover a deep third of the field', points: 2 },
      { label: 'Three linebackers drop into man coverage on the receivers', points: 0 },
      { label: 'The strong safety blitzes while two corners play deep halves', points: 0 },
    ],
  },
  {
    question: 'The offense lines up in "21 personnel." What does that mean?',
    options: [
      { label: '2 wide receivers, 1 tight end, and 2 running backs', points: 0 },
      { label: '2 running backs, 1 tight end, and 2 wide receivers', points: 2 },
      { label: '2 tight ends, 1 running back, and 2 wide receivers', points: 0 },
    ],
  },
  {
    question: 'On an RPO, the quarterback reads a specific defender after the snap. Why?',
    options: [
      { label: 'To decide whether to hand off or throw based on that defender\'s reaction', points: 2 },
      { label: 'To identify the mike linebacker for the offensive line\'s protection', points: 0 },
      { label: 'To call an audible and change the play at the line of scrimmage', points: 0 },
    ],
  },
];

const SOCCER_QUIZ: QuizQuestion[] = [
  {
    question: 'A team is "pressing high." What is the primary tactical objective?',
    options: [
      { label: 'Win the ball near the opponent\'s goal by closing down passing lanes quickly', points: 2 },
      { label: 'Push the defensive line up to catch attackers offside more often', points: 0 },
      { label: 'Keep possession by recycling the ball in the opponent\'s half', points: 0 },
    ],
  },
  {
    question: 'In a 4-2-3-1 formation, what role do the "2" typically fill?',
    options: [
      { label: 'Two center-backs who split wide to create passing angles in build-up', points: 0 },
      { label: 'Two central midfielders who shield the defense and control distribution', points: 2 },
      { label: 'Two wing-backs who provide width and push high in attack', points: 0 },
    ],
  },
  {
    question: 'A "false 9" drops deep into midfield. What tactical problem does this create?',
    options: [
      { label: 'It overloads the wing to create 2v1 situations out wide', points: 0 },
      { label: 'Center-backs are left marking empty space, opening a gap between lines', points: 2 },
      { label: 'It forces the keeper off the line to act as a sweeper', points: 0 },
    ],
  },
];

const BASKETBALL_QUIZ: QuizQuestion[] = [
  {
    question: 'A team runs a pick & roll and the defender "drops." What is the best counter?',
    options: [
      { label: 'Pull up for a mid-range jumper in the space the dropping big leaves open', points: 2 },
      { label: 'Pass the ball to the corner and reset the play from scratch', points: 0 },
      { label: 'Drive baseline away from the screen since the big is dropping back', points: 0 },
    ],
  },
  {
    question: 'Why is the "high post" considered the soft spot of a 2-3 zone?',
    options: [
      { label: 'It\'s the closest spot to the basket that\'s unguarded', points: 0 },
      { label: 'A player there can see the whole floor and force two defenders to react', points: 2 },
      { label: 'Zone defenders aren\'t allowed to guard the free-throw line area', points: 0 },
    ],
  },
  {
    question: 'A team runs "5-out" spacing. What tactical advantage does this create?',
    options: [
      { label: 'All five players outside the arc, maximizing driving lanes', points: 2 },
      { label: 'At least two players always near the basket for rebounds', points: 0 },
      { label: 'Prevents the defense from pressing full court', points: 0 },
    ],
  },
];

const BASEBALL_QUIZ: QuizQuestion[] = [
  {
    question: 'A 3-1 count generally favors:',
    options: [
      { label: 'The hitter — pitcher must throw a strike, often a fastball', points: 2 },
      { label: 'The pitcher — free pitch to waste', points: 0 },
      { label: 'Neither side', points: 0 },
    ],
  },
  {
    question: 'With a runner on first and one out, infielders typically play:',
    options: [
      { label: 'Standard depth', points: 0 },
      { label: '"Double-play depth" — closer to second for shorter throws', points: 2 },
      { label: 'On the outfield grass', points: 0 },
    ],
  },
  {
    question: 'OPS is the sum of:',
    options: [
      { label: 'OBP + SLG', points: 2 },
      { label: 'AVG + SLG', points: 0 },
      { label: 'HR + RBI', points: 0 },
    ],
  },
];

function getQuizLevel(score: number): 'beginner' | 'intermediate' | 'advanced' {
  if (score <= 2) return 'beginner';
  if (score <= 4) return 'advanced'; // 5-6 = advanced
  return 'advanced';
}

// Corrected scoring
function getQuizLevelFromScore(score: number): 'beginner' | 'intermediate' | 'advanced' {
  if (score <= 0) return 'beginner';
  if (score <= 2) return 'intermediate';
  return 'advanced';
}

const LEVELS = [
  {
    id: 'beginner' as const,
    label: 'Beginner',
    subtitle: 'New to the sport',
    description: 'Start with the basics — rules, positions, and formations',
    icon: BookOpen,
    color: 'text-primary',
    bg: 'bg-primary/10',
    border: 'border-primary/30',
  },
  {
    id: 'intermediate' as const,
    label: 'Intermediate',
    subtitle: 'Know the fundamentals',
    description: 'Deepen your knowledge with tactics and strategy',
    icon: TrendingUp,
    color: 'text-accent',
    bg: 'bg-accent/10',
    border: 'border-accent/30',
  },
  {
    id: 'advanced' as const,
    label: 'Advanced',
    subtitle: 'Student of the game',
    description: 'Master advanced schemes, reads, and game theory',
    icon: GraduationCap,
    color: 'text-success',
    bg: 'bg-success/10',
    border: 'border-success/30',
  },
];

interface SkillLevelPickerProps {
  sport: string;
  sportEmoji: string;
  sportLabel: string;
  onSelect: (level: 'beginner' | 'intermediate' | 'advanced') => void;
  onDismiss: () => void;
}

export function SkillLevelPicker({ sport, sportEmoji, sportLabel, onSelect, onDismiss }: SkillLevelPickerProps) {
  const [mode, setMode] = useState<'pick' | 'quiz' | 'result'>('pick');
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizScore, setQuizScore] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);

  const quizQuestions =
    sport === 'soccer' ? SOCCER_QUIZ
    : sport === 'basketball' ? BASKETBALL_QUIZ
    : sport === 'baseball' ? BASEBALL_QUIZ
    : FOOTBALL_QUIZ;
  const resultLevel = getQuizLevelFromScore(quizScore);
  const resultInfo = LEVELS.find(l => l.id === resultLevel)!;

  const handleQuizAnswer = (points: number, idx: number) => {
    setSelectedAnswer(idx);
    const newScore = quizScore + points;
    setTimeout(() => {
      setQuizScore(newScore);
      setSelectedAnswer(null);
      if (quizIndex < quizQuestions.length - 1) {
        setQuizIndex(i => i + 1);
      } else {
        setMode('result');
      }
    }, 600);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onDismiss}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-card border-2 border-border rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <AnimatePresence mode="wait">
          {mode === 'pick' && (
            <motion.div
              key="pick"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-6 space-y-5"
            >
              <div className="text-center space-y-2">
                <span className="text-4xl">{sportEmoji}</span>
                <h2 className="font-display font-extrabold text-xl text-foreground">
                  What's your {sportLabel} knowledge?
                </h2>
                <p className="text-sm text-muted-foreground">
                  We'll highlight the most relevant lessons for you
                </p>
              </div>

              <div className="space-y-2.5">
                {LEVELS.map(level => (
                  <button
                    key={level.id}
                    onClick={() => onSelect(level.id)}
                    className={`w-full flex items-center gap-3 rounded-xl border-2 ${level.border} ${level.bg} p-4 text-left transition-all hover:scale-[1.02] active:scale-[0.98]`}
                  >
                    <div className={`w-10 h-10 rounded-full ${level.bg} flex items-center justify-center`}>
                      <level.icon className={`w-5 h-5 ${level.color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-display font-bold text-sm text-foreground">{level.label}</p>
                      <p className="text-xs text-muted-foreground">{level.description}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                ))}
              </div>

              <div className="pt-2">
                <button
                  onClick={() => setMode('quiz')}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-all text-sm font-display font-bold"
                >
                  <HelpCircle className="w-4 h-4" />
                  Not sure? Take a quick quiz
                </button>
              </div>
            </motion.div>
          )}

          {mode === 'quiz' && (
            <motion.div
              key="quiz"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="p-6 space-y-5"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-display font-bold text-muted-foreground">
                  Question {quizIndex + 1} of {quizQuestions.length}
                </span>
                <button
                  onClick={() => setMode('pick')}
                  className="text-xs text-muted-foreground hover:text-foreground font-display font-bold"
                >
                  Skip quiz
                </button>
              </div>

              {/* Progress bar */}
              <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-primary rounded-full"
                  animate={{ width: `${((quizIndex + 1) / quizQuestions.length) * 100}%` }}
                  transition={{ duration: 0.3 }}
                />
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={quizIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-4"
                >
                  <p className="font-display font-bold text-sm text-foreground leading-relaxed">
                    {quizQuestions[quizIndex].question}
                  </p>

                  <div className="space-y-2">
                    {quizQuestions[quizIndex].options.map((opt, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectedAnswer === null && handleQuizAnswer(opt.points, idx)}
                        disabled={selectedAnswer !== null}
                        className={`w-full text-left rounded-xl border-2 p-3.5 text-sm transition-all ${
                          selectedAnswer === idx
                            ? opt.points > 0
                              ? 'border-success bg-success/10 text-foreground'
                              : 'border-destructive/50 bg-destructive/5 text-foreground'
                            : selectedAnswer !== null
                              ? 'opacity-50 border-border'
                              : 'border-border hover:border-primary/30 text-foreground'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          )}

          {mode === 'result' && (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="p-6 space-y-5 text-center"
            >
              <div className={`w-16 h-16 rounded-full ${resultInfo.bg} flex items-center justify-center mx-auto`}>
                <resultInfo.icon className={`w-8 h-8 ${resultInfo.color}`} />
              </div>
              <div>
                <h2 className="font-display font-extrabold text-xl text-foreground">
                  {resultInfo.label}
                </h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {resultInfo.description}
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                You scored {quizScore}/{quizQuestions.length * 2} · You can change this anytime in settings
              </p>
              <div className="space-y-2">
                <Button
                  onClick={() => onSelect(resultLevel)}
                  className="w-full h-12 font-display font-bold"
                >
                  <Check className="mr-2 w-4 h-4" />
                  Start as {resultInfo.label}
                </Button>
                <button
                  onClick={() => setMode('pick')}
                  className="text-xs text-muted-foreground hover:text-foreground font-display font-bold"
                >
                  Choose a different level
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
